import { Vector2, Vector3, Plane, BoxGeometry, MeshBasicMaterial, Mesh } from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { ReactiveComponent } from '../../core/ReactiveComponent.core.js';
import { computeProjectiveMatrix3d } from '../../plugins/3d-2d-transform/src/projectiveMatrix.js';
import { vapiPerspectiveCornerHandleStyle } from '../../constants/vapiPerspectiveHandles.js';
import { ComponentRegistry } from '../../constants/componentRegistry.js';

const DEFAULT_PRESET = '0:10:0';
const DEFAULT_WIDTH = 300;
const DEFAULT_HEIGHT = 52;

export function normalizeTimePreset(raw) {
    const str = String(raw || DEFAULT_PRESET).trim();
    const parts = str.split(':').map((p) => Math.max(0, parseInt(p, 10) || 0));
    while (parts.length < 3) parts.unshift(0);
    const [h, m, s] = parts.slice(-3);
    return `${h}:${m}:${s}`;
}

export function parseTimePresetSeconds(raw) {
    const preset = normalizeTimePreset(raw);
    const [h, m, s] = preset.split(':').map((p) => parseInt(p, 10) || 0);
    return h * 3600 + m * 60 + s;
}

export function formatCountdown(totalSeconds) {
    const safe = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    const h = Math.floor(safe / 3600);
    const m = Math.floor((safe % 3600) / 60);
    const s = safe % 60;
    return [
        String(h).padStart(2, '0'),
        String(m).padStart(2, '0'),
        String(s).padStart(2, '0'),
    ].join(':');
}

/**
 * VRTimerElement - CSS3D countdown timer with perspective warping (like VRIframeElement).
 */
class VRTimerElement extends ReactiveComponent {
    constructor(params = {}) {
        super(null, {
            mode: params.editMode ? 'edit' : 'view',
        });

        const { model, camera, controls, raycaster, position, id, dbConfig, sizes } = params;
        this.id = id;
        this.model = model;
        this.camera = camera;
        this.controls = controls || null;
        this.position = position || { x: 0, y: 0, z: -540 };
        this.dbConfig = dbConfig || {};
        this._raycaster = raycaster || null;
        this._sizes = sizes || { width: window.innerWidth, height: window.innerHeight };
        this._viewportEl = params.viewportElement || document.querySelector('#webgl') || document.querySelector('#css3d');
        this._ndc = new Vector2();
        this._dragPlane = new Plane();
        this._dragPlaneNormal = new Vector3();
        this._planeIntersectPoint = new Vector3();
        this._dragGrabOffset = new Vector3();
        this._tmpVec = new Vector3();
        this._dragStartDistance = 0;
        this.isDragging = false;
        this._captureEl = null;
        this._isEditMode = !!params.editMode;
        this._css3dScale = 1;
        this._userScale = Number(this.dbConfig?.size ?? this.dbConfig?.scale) || 1;
        this._cornerHandles = [];
        this._intervalId = null;
        this._isRunning = false;
        this._remainingSeconds = parseTimePresetSeconds(this.dbConfig?.timePreset);
        this.parent = this;
        this.name = 'timerElement';
        this.type = 'timerElement';
        this.objConfig = {
            isResizable: true,
            isRotatable: true,
        };

        this._buildNode();
        this.anchor.position.set(
            this.position.x,
            this.position.y,
            this.position.z !== undefined ? this.position.z : -540
        );
        this.position = this.anchor.position;
        this.anchor.userData.vrTimerElement = this;

        if (this.state.mode === 'edit') {
            this._attachDragListeners();
        }
        this._applyVisibilityFromConfig();
        this._updateDisplay();
        return this;
    }

    _buildNode() {
        const w = this._domW = Number(this.dbConfig?.width) > 0 ? Number(this.dbConfig.width) : DEFAULT_WIDTH;
        const h = this._domH = Number(this.dbConfig?.height) > 0 ? Number(this.dbConfig.height) : DEFAULT_HEIGHT;
        this._originalPos = [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];

        const dataId = `timer_element_${this.id}`;
        Array.from(document.querySelectorAll(`[data-id='${dataId}']`)).forEach((node) => node?.remove?.());

        const root = document.createElement('div');
        root.setAttribute('class', 'vrai-agent-component vr-timer-element-wrapper css3d_test_wrapper');
        root.setAttribute('data-css3d-interactive', 'true');
        root.setAttribute(
            'data-css3d-click-target',
            '.vr-timer-element-perspective-content, .vr-timer-element-corner-handle'
        );
        root.setAttribute('data-id', dataId);
        root.style.cssText = `
            position: relative;
            width: ${w}px;
            height: ${h}px;
            pointer-events: auto;
            will-change: transform;
            user-select: none;
            -webkit-user-select: none;
        `;
        root.classList.add(`vr-timer-element-${this.id}`);

        if (this._isEditMode) {
            root.addEventListener('click', (e) => e.stopPropagation());
        }

        root.addEventListener('pointerdown', (e) => {
            if (e.button !== 0) return;
            if (e.target?.closest?.('.vr-timer-element-corner-handle')) return;
            const hitPerspective = e.target?.closest?.('.vr-timer-element-perspective-content')
                ?? document.elementFromPoint(e.clientX, e.clientY)?.closest?.('.vr-timer-element-perspective-content');
            if (this.state.mode === 'edit' && hitPerspective && this._onPointerDown) {
                e.preventDefault();
                e.stopPropagation();
                this._onPointerDown(e);
            }
        });

        const saved = this.dbConfig?.perspectiveOffsets;
        this._perspectiveCorners = Array.isArray(saved) && saved.length === 4
            ? saved.map((p) => (p.xr !== undefined
                ? { x: Number(p.xr) * w, y: Number(p.yr) * h }
                : { x: Number(p.x) ?? p.x, y: Number(p.y) ?? p.y }))
            : this._originalPos.map((p) => ({ ...p }));

        const timerContent = document.createElement('div');
        timerContent.className = 'vr-timer-element vapi-agent-top-status world-bottom-nav__area__item world-bottom-nav__area__location status-side status-agent vrai-status-agent is-visible';
        timerContent.innerHTML = `
            <span class="material-icons-outlined vapi-agent-top-status__icon" aria-hidden="true">timer</span>
            <div class="divider" aria-hidden="true"></div>
            <span class="vapi-agent-top-status__text vr-timer-element__time">00:00:00</span>
        `;
        this._timeEl = timerContent.querySelector('.vr-timer-element__time');

        const perspectiveContent = document.createElement('div');
        perspectiveContent.className = 'vr-timer-element-perspective-content';
        perspectiveContent.style.cssText = `position:absolute;inset:0;overflow:hidden;transform-origin:0 0;transform-style:flat;will-change:transform;pointer-events:auto;`;
        if (this._isEditMode) perspectiveContent.style.cursor = 'grab';
        perspectiveContent.appendChild(timerContent);
        this._perspectiveContent = perspectiveContent;

        const perspectiveWrapper = document.createElement('div');
        perspectiveWrapper.className = 'vr-timer-element-perspective-wrapper';
        perspectiveWrapper.style.cssText = 'position:absolute;inset:0;overflow:visible;pointer-events:none;';
        this._perspectiveWrapper = perspectiveWrapper;
        perspectiveWrapper.appendChild(perspectiveContent);

        this._applyPerspectiveTransform = () => {
            const ew = root.offsetWidth || this._domW;
            const eh = root.offsetHeight || this._domH;
            const currentOrigPos = [{ x: 0, y: 0 }, { x: ew, y: 0 }, { x: ew, y: eh }, { x: 0, y: eh }];
            const m = computeProjectiveMatrix3d(currentOrigPos, this._perspectiveCorners);
            perspectiveContent.style.transform = m;
            this._syncHandlePositions?.();
        };
        this._applyPerspectiveTransform();

        if (this.state.mode === 'edit') {
            for (let i = 0; i < 4; i++) {
                const hnd = document.createElement('div');
                hnd.className = 'vr-timer-element-corner-handle';
                hnd.dataset.corner = String(i);
                hnd.style.cssText = vapiPerspectiveCornerHandleStyle('pointer-events:auto;');
                const updateHandlePos = () => {
                    hnd.style.left = `${this._perspectiveCorners[i].x}px`;
                    hnd.style.top = `${this._perspectiveCorners[i].y}px`;
                };
                updateHandlePos();
                const onCornerPointerDown = (e) => {
                    if (this.dbConfig?.lockedInEdit) return;
                    e.preventDefault();
                    e.stopPropagation();
                    hnd.style.cursor = 'grabbing';
                    const startX = e.clientX;
                    const startY = e.clientY;
                    const startPos = { x: this._perspectiveCorners[i].x, y: this._perspectiveCorners[i].y };
                    const onMove = (ev) => {
                        const dx = ev.clientX - startX;
                        const dy = ev.clientY - startY;
                        this._perspectiveCorners[i].x = startPos.x + dx;
                        this._perspectiveCorners[i].y = startPos.y + dy;
                        updateHandlePos();
                        this._applyPerspectiveTransform();
                    };
                    const onUp = () => {
                        hnd.style.cursor = 'grab';
                        document.removeEventListener('pointermove', onMove);
                        document.removeEventListener('pointerup', onUp);
                        this.dbConfig = {
                            ...this.dbConfig,
                            perspectiveOffsets: this._perspectiveOffsetsForDb(),
                        };
                        if (this.model?.view?.updateJSON) this.model.view.updateJSON(this.anchor, null);
                    };
                    document.addEventListener('pointermove', onMove);
                    document.addEventListener('pointerup', onUp);
                };
                hnd.addEventListener('pointerdown', onCornerPointerDown);
                perspectiveWrapper.appendChild(hnd);
                this._cornerHandles.push(hnd);
            }
        }

        this._syncHandlePositions = () => {
            this._cornerHandles.forEach((hnd, i) => {
                if (this._perspectiveCorners[i]) {
                    hnd.style.left = `${this._perspectiveCorners[i].x}px`;
                    hnd.style.top = `${this._perspectiveCorners[i].y}px`;
                }
            });
        };

        root.appendChild(perspectiveWrapper);

        this.root = root;
        this.dom = root;
        this.css3dObject = new CSS3DObject(root);
        const initialScale = (this._css3dScale = this._getViewportScale()) * (this._userScale ?? 1);
        this.css3dObject.scale.set(initialScale, initialScale, 1);
        this._syncCornerHandleInverseScale();

        const geo = new BoxGeometry(0.1, 0.1, 0.1);
        const mat = new MeshBasicMaterial({ visible: false });
        this.anchor = new Mesh(geo, mat);
        this.anchor.name = 'timerElement';
        this.anchor.type = 'timerElement';
        this.anchor.groupDetails = {
            title: (typeof $trans !== 'undefined' && $trans('timer-text')) || 'Timer',
            icon: 'timer',
            editTitle: (typeof $trans !== 'undefined' && $trans('edit-timer-element')) || 'Edit timer',
            dataLang: 'edit-timer-element',
        };
        this.anchor.add(this.css3dObject);

        this.elementClass = `vr-timer-element-${this.id}`;
        this.mesh = this.anchor;
        this.object = this.anchor;
    }

    _getEventSourceId() {
        return this.dbConfig?.id ?? this.getId?.() ?? this.id;
    }

    _fireTimerEvent(eventType) {
        if (!eventType || this.model?.editMode) return;
        const sourceId = this._getEventSourceId();
        if (sourceId == null) return;
        this.model?.view?._executeEventActions?.(sourceId, eventType);
    }

    _updateDisplay() {
        if (this._timeEl) {
            this._timeEl.textContent = formatCountdown(this._remainingSeconds);
        }
    }

    _getPresetSeconds() {
        return parseTimePresetSeconds(this.dbConfig?.timePreset);
    }

    startTimer() {
        if (this._isRunning) return;
        if (this._remainingSeconds <= 0) {
            this._remainingSeconds = this._getPresetSeconds();
            this._updateDisplay();
        }
        this._isRunning = true;
        this._fireTimerEvent('timerStarted');
        if (this._intervalId) clearInterval(this._intervalId);
        this._intervalId = setInterval(() => {
            if (this._remainingSeconds <= 0) {
                this._onTimerEnded();
                return;
            }
            this._remainingSeconds -= 1;
            this._updateDisplay();
            if (this._remainingSeconds <= 0) {
                this._onTimerEnded();
            }
        }, 1000);
    }

    stopTimer() {
        if (!this._isRunning && !this._intervalId) return;
        this._isRunning = false;
        if (this._intervalId) {
            clearInterval(this._intervalId);
            this._intervalId = null;
        }
        this._fireTimerEvent('timerStopped');
    }

    resetTimer() {
        this._isRunning = false;
        if (this._intervalId) {
            clearInterval(this._intervalId);
            this._intervalId = null;
        }
        this._remainingSeconds = this._getPresetSeconds();
        this._updateDisplay();
    }

    _onTimerEnded() {
        this._isRunning = false;
        if (this._intervalId) {
            clearInterval(this._intervalId);
            this._intervalId = null;
        }
        this._remainingSeconds = 0;
        this._updateDisplay();
        this._fireTimerEvent('timerEnded');
    }

    _perspectiveOffsetsForDb() {
        const ew = this.root?.offsetWidth || this._domW || 1;
        const eh = this.root?.offsetHeight || this._domH || 1;
        return (this._perspectiveCorners || []).map((p) => ({
            x: p.x,
            y: p.y,
            xr: p.x / ew,
            yr: p.y / eh,
        }));
    }

    _getViewportScale() {
        const vpEl = this._viewportEl;
        const canvasH = vpEl?.clientHeight || 0;
        const winH = window.innerHeight;
        return (canvasH > 0 && winH > canvasH) ? winH / canvasH : 1;
    }

    _syncCornerHandleInverseScale() {
        if (!this.css3dObject) return;
        const s = Math.max(this.css3dObject.scale.x || 1, 0.02);
        const inv = 1 / s;
        const t = `translate(-50%,-50%) scale(${inv})`;
        for (const hnd of this._cornerHandles) {
            hnd.style.transform = t;
        }
    }

    _applyVisibilityFromConfig() {
        // Logic-only in view mode: countdown UI is edit-only; never show in ModeView.
        const inEditMode = !!this.model?.editMode || this.state.mode === 'edit';
        this.anchor.visible = inEditMode;
        if (this.css3dObject) this.css3dObject.visible = inEditMode;
        this._syncDomVisibilityToAnchor();
    }

    _syncDomVisibilityToAnchor() {
        if (!this.dom?.style) return;
        const visible = this.anchor?.visible !== false;
        this.dom.classList.toggle('visible', visible);
        this.dom.classList.toggle('hide-on-start', !visible);
        this.dom.style.display = visible ? '' : 'none';
    }

    _clientToNDC(clientX, clientY) {
        const el = this._viewportEl;
        if (!el) {
            this._ndc.x = (clientX / this._sizes.width) * 2 - 1;
            this._ndc.y = -(clientY / this._sizes.height) * 2 + 1;
            return;
        }
        const rect = el.getBoundingClientRect();
        this._ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this._ndc.y = 1 - ((clientY - rect.top) / rect.height) * 2;
    }

    _setFromClientXY(clientX, clientY) {
        this._clientToNDC(clientX, clientY);
        this._raycaster.setFromCamera(this._ndc, this.camera);
        if (this._raycaster.ray.intersectPlane(this._dragPlane, this._planeIntersectPoint)) {
            this._tmpVec.copy(this._planeIntersectPoint).add(this._dragGrabOffset);
            this._tmpVec.sub(this.camera.position).normalize();
            this._tmpVec.multiplyScalar(this._dragStartDistance).add(this.camera.position);
            if (this.anchor.parent) {
                this.anchor.parent.updateMatrixWorld(true);
                this.anchor.parent.worldToLocal(this._tmpVec);
                this.anchor.position.copy(this._tmpVec);
            } else {
                this.anchor.position.copy(this._tmpVec);
            }
        }
    }

    _onPointerMove = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.isDragging) return;
        this._setFromClientXY(e.clientX, e.clientY);
        this.anchor.lookAt(this.camera.position);
    };

    _onPointerUp = (e) => {
        if (!this.isDragging) return;
        this.isDragging = false;
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grab';
        if (this._captureEl) {
            try { this._captureEl.releasePointerCapture(e.pointerId); } catch (err) { }
            this._captureEl = null;
        }
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = true;
        if (this.model?.view) {
            if (this.model.view.updateJSON) this.model.view.updateJSON(this.anchor, null);
            this.model.view.elementSelected = this;
            this.showObjectMenu();
            if (typeof $ !== 'undefined') $('.edit_element').trigger('click');
        }
    };

    _onPointerDown = (e) => {
        if (this.state.mode !== 'edit') return;
        if (this.dbConfig?.lockedInEdit) return;
        if (this.isDragging) return;
        if (e.target?.closest?.('.vr-timer-element-corner-handle')) return;
        this.isDragging = true;
        this._captureEl = e.currentTarget || this.dom;
        try { this._captureEl?.setPointerCapture?.(e.pointerId); } catch (err) { }
        if (this.anchor.parent) {
            this.anchor.parent.updateMatrixWorld(true);
            this.anchor.getWorldPosition(this._tmpVec);
        } else {
            this._tmpVec.copy(this.anchor.position);
        }
        this._dragPlaneNormal.subVectors(this._tmpVec, this.camera.position).normalize();
        this._dragPlane.setFromNormalAndCoplanarPoint(this._dragPlaneNormal, this._tmpVec);
        this._dragStartDistance = this.camera.position.distanceTo(this._tmpVec);
        this._clientToNDC(e.clientX, e.clientY);
        this._raycaster.setFromCamera(this._ndc, this.camera);
        if (this._raycaster.ray.intersectPlane(this._dragPlane, this._planeIntersectPoint)) {
            this._dragGrabOffset.copy(this._tmpVec).sub(this._planeIntersectPoint);
        } else {
            this._dragGrabOffset.set(0, 0, 0);
        }
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grabbing';
        window.addEventListener('pointermove', this._onPointerMove);
        window.addEventListener('pointerup', this._onPointerUp);
        window.addEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = false;
    };

    _attachDragListeners() {
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grab';
    }

    _detachDragListeners() {
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = '';
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        this.isDragging = false;
        if (this.controls) this.controls.enabled = true;
    }

    render(camera) {
        if (!camera) return;
        if (!this.isDragging) {
            this.anchor.lookAt(camera.position);
            this._css3dScale = this._getViewportScale();
            const scale = this._css3dScale * (this._userScale ?? 1);
            if (this.css3dObject) this.css3dObject.scale.set(scale, scale, 1);
        }
        this._syncCornerHandleInverseScale();
    }

    getId() {
        return this.id;
    }

    setId(id) {
        this.id = id;
    }

    getDbConfig() {
        return this.dbConfig;
    }

    setDbConfig(config) {
        this.dbConfig = { ...this.dbConfig, ...config };
        if (config?.timePreset !== undefined) {
            this.dbConfig.timePreset = normalizeTimePreset(config.timePreset);
            if (!this._isRunning) {
                this._remainingSeconds = this._getPresetSeconds();
                this._updateDisplay();
            }
        }
        if (config?.size !== undefined || config?.scale !== undefined) {
            const s = config.size ?? config.scale;
            this._userScale = Number(s) > 0 ? Number(s) : 1;
        }
        if (config?.perspectiveOffsets && Array.isArray(config.perspectiveOffsets) && this._perspectiveCorners) {
            const w = this.root?.offsetWidth || this._domW || 1;
            const h = this.root?.offsetHeight || this._domH || 1;
            this._perspectiveCorners.splice(0, 4, ...config.perspectiveOffsets.map((p) => (p.xr !== undefined && p.yr !== undefined)
                ? { x: Number(p.xr) * w, y: Number(p.yr) * h }
                : { x: Number(p.x) ?? p.x, y: Number(p.y) ?? p.y }));
            this._syncHandlePositions?.();
            this._applyPerspectiveTransform?.();
        }
        if (config?.width > 0 || config?.height > 0) {
            const newW = Number(config.width) > 0 ? Number(config.width) : this._domW;
            const newH = Number(config.height) > 0 ? Number(config.height) : this._domH;
            if (this.root) {
                this.root.style.width = `${newW}px`;
                this.root.style.height = `${newH}px`;
                this._domW = newW;
                this._domH = newH;
                this._originalPos = [{ x: 0, y: 0 }, { x: newW, y: 0 }, { x: newW, y: newH }, { x: 0, y: newH }];
            }
        }
        this._applyVisibilityFromConfig();
    }

    getLimits() {
        return { min: 280, max: 800, distance: Number(this.dbConfig?.distance) || 540 };
    }

    getAxisRotations() {
        return { rotateX: 0, rotateY: 0 };
    }

    setDistance(val) {
        const n = Number(val);
        if (!Number.isFinite(n) || n <= 0 || !this.anchor?.position?.setLength) return;
        const { min, max } = this.getLimits();
        const clamped = Math.min(max, Math.max(min, n));
        this.anchor.position.setLength(clamped);
        this.dbConfig = { ...this.dbConfig, distance: clamped };
    }

    setSize(val) {
        const n = Number(val);
        if (!Number.isFinite(n) || n <= 0) return;
        this._userScale = n;
        this.dbConfig = { ...this.dbConfig, size: n };
    }

    setAxisRotations() { }

    showObjectMenu() {
        const title = (typeof $trans !== 'undefined' && $trans('timer-text')) || 'Timer';
        $('.object-menu .object-name').text(title);
        $('.object-menu').addClass('is-active');
        $('.object-menu').addClass('edit');
        $('.timer-element-settings').removeClass('hidden').addClass('is-active');
        $('.vrai-agent-settings, .tooltip-settings, .video-loop, .video-transparent, .iframe-agent-settings').addClass('hidden');
        $('.perspective-settings').removeClass('hidden');
    }

    toggleSelect() {
        this.dom?.querySelector('.vr-timer-element')?.classList?.toggle('active');
    }

    resetPerspective() {
        if (!this._perspectiveCorners || !this._originalPos) return;
        this._perspectiveCorners.splice(0, 4, ...this._originalPos.map((p) => ({ ...p })));
        this._syncHandlePositions?.();
        this._applyPerspectiveTransform?.();
        this.dbConfig = { ...this.dbConfig, perspectiveOffsets: this._perspectiveOffsetsForDb() };
    }

    destroy() {
        this.stopTimer();
        try { this._detachDragListeners(); } catch (e) { }
        if (this.dom?.parentNode) this.dom.parentNode.removeChild(this.dom);
        document.querySelectorAll(`[data-id='timer_element_${this.id}']`).forEach((node) => node?.remove?.());
    }
}

ComponentRegistry.register({
    type: 'timerElement',
    displayNameKey: 'timer-text',
    getDisplayName: () => (typeof $trans !== 'undefined' && $trans('timer-text')) || 'Timer',
    events: [
        { value: 'timerStarted', labelKey: 'event-timer-started' },
        { value: 'timerStopped', labelKey: 'event-timer-stopped' },
        { value: 'timerEnded', labelKey: 'event-timer-ended' },
        { value: 'raycastEnter', labelKey: 'event-raycast-enter' },
        { value: 'raycastLeave', labelKey: 'event-raycast-leave' },
    ],
    actions: [
        { value: 'startTimer', labelKey: 'action-start-timer' },
        { value: 'stopTimer', labelKey: 'action-stop-timer' },
        { value: 'resetTimer', labelKey: 'action-reset-timer' },
    ],
});

export { VRTimerElement };
